import type { Load } from '@sveltejs/kit';
import type { Locale } from '@agapi/mlc-core';
import type { ProductCollectionResult } from '../../../types/collections';
import type { Response } from '..';

import { error } from '@sveltejs/kit';
import { browser } from '$app/environment';

import { PUBLIC_INCLUDE_BRAND_SLUG_IN_COLLECTIONS, PUBLIC_BRAND_SLUG } from '$env/static/public';

import { getCommerce } from '../../../services';
import { getCookie } from '../../../utils/cookies.client';
import { getLocaleFromUrl, removeLocaleFromPath } from '../../../utils/locale.shared';
import { getFacetsAndSort, DEFAULT_LIMIT } from '../../../utils/collections.shared';

const getFullCollectionSlug = (url: URL): string => {
  return PUBLIC_INCLUDE_BRAND_SLUG_IN_COLLECTIONS === 'true' ? `/${PUBLIC_BRAND_SLUG}${removeLocaleFromPath(url.pathname)}` : removeLocaleFromPath(url.pathname);
}

const getProducts = async (url: URL, fetch: typeof globalThis.fetch, identifier: string, locale: Locale): Promise<ProductCollectionResult> => {
  const fullCollectionSlug = getFullCollectionSlug(url);
  const { filterFacets, sortBy } = getFacetsAndSort(url, locale);

  const response = await getCommerce(fetch).getProducts(
    {
      attribute: { name: 'collections_full_slugs', value: fullCollectionSlug },
      facetFilter: filterFacets,
      sortBy: sortBy,
      offset: 0,
      limit: DEFAULT_LIMIT,
    },
    locale,
    { userId: identifier }
  );

  return { ...response, filtersSelected: filterFacets };
}

export const load: Load<{}, Pick<Response, 'collection'>, {}, Response, 'paths'> = async ({ url, fetch, data }) => {
  const identifier = browser ? getCookie<string>('__agapi_identifier') ?? 'default' : 'default';
  const locale = getLocaleFromUrl(url);
  const products = await getProducts(url, fetch, identifier, locale);

  // Throw error if collection is empty (will be caught by route-level error handling)
  if (products?.count === 0) {
    error(404, "Collection is empty");
  }

  return { products, collection: data.collection };
};
